if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();

        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      connectedCallback() {
        if (this.dataset.mmPreview === 'true') this.initPreview();
      }

      initPreview() {
        if (!this.isConnected || this.previewReady) return;
        this.previewReady = true;
        this.previewGeneration = (this.previewGeneration || 0) + 1;
        this.bindPreviewModals();
        if (this.dataset.mmSurface === 'pdp') {
          this.previewUnsubscribe = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            (event) => { if (!event.cart?.errors) this.refreshPreview(); }
          );
        }
        this.enrichPreview();
      }


      previewContextKey() {
        return [location.origin, document.documentElement.lang, window.Shopify?.country, window.Shopify?.currency?.active].join('|');
      }

      previewSlotKey(card) {
        return JSON.stringify([card.dataset.mmSourceId, card.dataset.mmCandidateId, card.dataset.mmSourceRevision]);
      }

      async enrichPreview() {
        if (!this.isConnected || !this.dataset.nativeUrl) return;
        const generation = this.previewGeneration;
        const context = this.previewContextKey();
        const cards = Array.from(this.querySelectorAll('[data-mm-native-eligible="true"]')).filter((card) => card.querySelector('template[data-mm-deferred-view]'));
        const seeds = Array.from(new Set(cards.map((card) => card.dataset.mmSourceId)));
        if (!cards.length || cards.length > 3 || seeds.length > 3 || seeds.some((seed) => !/^\d+$/.test(seed))) return;
        this.nativeAbort?.abort();
        const controller = new AbortController();
        this.nativeAbort = controller;
        const timeout = setTimeout(() => controller.abort(), 10000);
        const slots = cards.map((card) => ({ key: this.previewSlotKey(card), source: card.dataset.mmSourceId, replacement: null }));
        try {
          await Promise.all(seeds.map(async (seed) => {
            try {
              const url = new URL(this.dataset.nativeUrl, location.origin);
              if (url.origin !== location.origin) return;
              url.searchParams.set('product_id', seed);
              const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
              if (!response.ok) return;
              const html = await response.text();
              if (!this.isConnected || generation !== this.previewGeneration || context !== this.previewContextKey()) return;
              const owner = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
              if (!owner || owner.dataset.mmNativeResponseSource !== seed) return;
              const nativeCards = Array.from(owner.querySelectorAll('[data-mm-native-card="true"]'));
              slots.filter((slot) => slot.source === seed).forEach((slot) => {
                const matches = nativeCards.filter((card) => this.previewSlotKey(card) === slot.key);
                if (matches.length === 1 && !matches[0].querySelector('template[data-mm-deferred-view]')) slot.replacement = matches[0];
              });
            } catch (error) {
            }
          }));
          if (!this.isConnected || generation !== this.previewGeneration || context !== this.previewContextKey()) return;
          this.pendingNative = { generation, context, slots };
          this.flushNative();
        } finally {
          clearTimeout(timeout);
        }
      }

      flushNative() {
        const pending = this.pendingNative;
        if (!pending || !this.isConnected || pending.generation !== this.previewGeneration || pending.context !== this.previewContextKey()) return;
        if (this.hasActivePreviewInteraction()) return;
        const active = document.activeElement;
        const focused = this.contains(active) ? active.id : '';
        const hadFocus = this.contains(active);
        this.pendingNative = null;
        this.previewObserver?.disconnect();
        pending.slots.forEach((slot) => {
          const current = Array.from(this.querySelectorAll('[data-mm-native-eligible="true"]')).find((card) => this.previewSlotKey(card) === slot.key);
          if (!current || !current.querySelector('template[data-mm-deferred-view]')) return;
          if (slot.replacement) {
            current.replaceWith(slot.replacement);
          } else {
            current.querySelectorAll('template[data-mm-deferred-view]').forEach((template) => template.replaceWith(template.content));
          }
        });
        this.bindPreviewModals();
        if (hadFocus && document.activeElement === document.body) {
          const target = focused ? this.querySelector(`#${CSS.escape(focused)}`) : null;
          const fallback = this.closest('product-info')?.querySelector('form[is="product-form"] button[name="add"]:not([disabled])') || this;
          (target || fallback).focus({ preventScroll: true });
        }
      }

      hasActivePreviewInteraction() {
        if (this.previewModals.some((modal) => !modal.hidden || !this.contains(modal))) return true;
        const modalIds = new Set(this.previewModals.map((modal) => modal.id));
        return Array.from(this.querySelectorAll('[aria-controls][aria-busy]')).some((opener) =>
          opener.getAttribute('aria-busy') !== 'false' && modalIds.has(opener.getAttribute('aria-controls'))
        );
      }

      bindPreviewModals() {
        this.previewObserver?.disconnect();
        this.previewModals = Array.from(this.querySelectorAll('quick-view-modal'));
        this.previewObserver = new MutationObserver(() => {
          requestAnimationFrame(() => { this.flushPreview(); this.flushNative(); });
        });
        this.previewObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-busy'] });
        this.previewModals.forEach((modal) => this.previewObserver.observe(modal, { attributes: true, attributeFilter: ['hidden', 'open', 'inert'] }));
      }

      flushPreview() {
        const pending = this.pendingPreview;
        if (!pending || !this.isConnected || pending.generation !== this.previewGeneration || pending.context !== this.previewContextKey()) return;
        if (this.hasActivePreviewInteraction()) return;
        const active = document.activeElement;
        const focused = this.contains(active) ? active.id : '';
        const hadFocus = this.contains(active);
        this.pendingPreview = null;
        this.previewObserver?.disconnect();
        this.innerHTML = pending.html;
        this.hidden = false;
        this.bindPreviewModals();
        this.enrichPreview();
        if (hadFocus && document.activeElement === document.body) {
          const target = focused ? this.querySelector(`#${CSS.escape(focused)}`) : null;
          const fallback = this.closest('product-info')?.querySelector('form[is="product-form"] button[name="add"]:not([disabled])') || this;
          (target || fallback).focus({ preventScroll: true });
        }
      }

      async refreshPreview() {
        const generation = ++this.previewGeneration;
        const context = this.previewContextKey();
        this.nativeAbort?.abort();
        this.pendingNative = null;
        this.previewAbort?.abort();
        const controller = new AbortController();
        this.previewAbort = controller;
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(this.dataset.url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw new Error('Recommendation section unavailable');
          const html = await response.text();
          if (!this.isConnected || generation !== this.previewGeneration || context !== this.previewContextKey()) return;
          const replacement = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
          if (!replacement) throw new Error('Recommendation section missing');
          this.pendingPreview = { generation, context, html: replacement.innerHTML };
          this.flushPreview();
        } catch (error) {
          if (this.isConnected && generation === this.previewGeneration && context === this.previewContextKey()) {
            this.pendingPreview = { generation, context, html: '' };
            this.flushPreview();
          }
        } finally {
          clearTimeout(timeout);
        }
      }

      disconnectedCallback() {
        this.previewGeneration = (this.previewGeneration || 0) + 1;
        this.nativeAbort?.abort();
        this.pendingNative = null;
        this.previewAbort?.abort();
        this.previewUnsubscribe?.();
        this.previewObserver?.disconnect();
        this.pendingPreview = null;
        this.previewReady = false;
      }

      init() {
        if (this.dataset.mmPreview === 'true') { this.initPreview(); return; }
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((responseText) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector('.shopify-section');

            if (sectionInnerHTML === null) return;

            const recommendations = sectionInnerHTML.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
            }

            if (recommendations.querySelector('.product-card')) {
              this.dispatchEvent(new CustomEvent('recommendations:loaded'));
            } else {
              if (recommendations.classList.contains('complementary-products')) {
                this.closest('.product__block')?.remove();
              } else {
                this.closest('.shopify-section')?.remove();
              }
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    }
  );
}
